import Header from '../Header/Header';
import { ICONS, Loader } from '@ohif/ui';
import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import { Formik, Form, ErrorMessage, Field } from 'formik';
import { SuccessModal, Select } from '@ohif/ui';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _commonUrls, _teleradiologyuserUrls, _userUrls } from '@custom/Utils/constants/apiUrls';
import { servicesManager } from '../../app/src/App';
import * as Yup from 'yup'
import { CONTACT_NUMBER, EMAIL, phoneNumberRegex, LICENSE_NUMBER, RADIOLOGIST, NAME, YEARS_OF_EXP, AVAILABILITY, DOB, TO_TIME_AVAILABILITY, FROM_TIME_AVAILABILITY } from '../Utils/schema/schema';
import { customStyle, formatDate, getCurrencyContact, getLocalPhoneNumber } from '@custom/Utils/utils';
import teleradiologistApi from '@custom/rtk-apis/teleradiologist/getTeleradiologist';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField } from '@mui/material';
import { useTheme } from "../../ui/src/contextProviders"
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';

const AddEditRadiologist = () => {
    const getValidationSchema = (role: any) => {
        return Yup.object().shape({
            name: Yup.string().required(NAME.validation.required),
            radiologistType: Yup.string().required(RADIOLOGIST.validation.required),
            userRole: Yup.string().required("User role is required"),
            hprid: Yup.string().nullable(),
            gender: Yup.string().nullable(),
            email: Yup.string().email().required(EMAIL.validation.required).test('no-uppercase', EMAIL.validation.invalidCaps, value => {
                return !value || value === value.toLowerCase();
            }),
            contactNumber: Yup.string().matches(phoneNumberRegex).required(CONTACT_NUMBER.validation.valid),
            dob: Yup.date().required(DOB.validation.required).nullable()
                .test('isValid', 'Date of birth is required', (value) => value && dayjs(value).isValid()),
            department: Yup.string().nullable(),
            specialization: Yup.array().nullable(),
            yearsOfExperience: Yup.number().nullable().positive(YEARS_OF_EXP.validation.positive).integer(YEARS_OF_EXP.validation.integer),
            whatsappNumber: Yup.string().matches(phoneNumberRegex, CONTACT_NUMBER.validation.valid),
            licenseNumber: role === 'Admin' || role === 'Front Desk'
                ? Yup.string().nullable()
                : Yup.string().required(LICENSE_NUMBER.validation.required),
            availabilityDay: Yup.array()
                .min(1, AVAILABILITY.validation.required)
                .required(AVAILABILITY.validation.required)
                .nullable(),
            modality: Yup.array()
                .min(1, 'Modality is required')
                .required('Modality is required'),
            fromTimeAvailability: Yup.string().required(FROM_TIME_AVAILABILITY.validation.required),
            toTimeAvailability: Yup.string().required(TO_TIME_AVAILABILITY.validation.required).test(
                'is-greater',
                'To time must be later than from time',
                function (value) {
                    const { fromTimeAvailability } = this.parent;
                    if (!fromTimeAvailability || !value) return true;

                    const [fromHours, fromMinutes] = fromTimeAvailability.split(':');
                    const [toHours, toMinutes] = value.split(':');

                    const fromTotalMinutes = parseInt(fromHours, 10) * 60 + parseInt(fromMinutes, 10);
                    const toTotalMinutes = parseInt(toHours, 10) * 60 + parseInt(toMinutes, 10);

                    return toTotalMinutes > fromTotalMinutes;
                }
            ),
        });
    };

    const getResidentValidationSchema = (role: any) => {
        return Yup.object().shape({
            name: Yup.string().required(NAME.validation.required),
            radiologistType: Yup.string().required(RADIOLOGIST.validation.required),
            userRole: Yup.string().required("User role is required"),
            hprid: Yup.string().nullable(),
            gender: Yup.string().nullable(),
            email: Yup.string().email().required(EMAIL.validation.required).test('no-uppercase', EMAIL.validation.invalidCaps, value => {
                return !value || value === value.toLowerCase();
            }),
            contactNumber: Yup.string().matches(phoneNumberRegex).required(CONTACT_NUMBER.validation.valid),
            dob: Yup.date().required(DOB.validation.required).nullable()
                .test('isValid', 'Date of birth is required', (value) => value && dayjs(value).isValid()),
            department: Yup.string().nullable(),
            specialization: Yup.array().nullable(),
            yearsOfExperience: Yup.number().nullable().positive(YEARS_OF_EXP.validation.positive).integer(YEARS_OF_EXP.validation.integer),
            whatsappNumber: Yup.string().matches(phoneNumberRegex, CONTACT_NUMBER.validation.valid),
            availabilityDay: Yup.array()
                .min(1, AVAILABILITY.validation.required)
                .required(AVAILABILITY.validation.required)
                .nullable(),
            fromTimeAvailability: Yup.string().required(FROM_TIME_AVAILABILITY.validation.required),
            toTimeAvailability: Yup.string().required(TO_TIME_AVAILABILITY.validation.required).test(
                'is-greater',
                'To time must be later than from time',
                function (value) {
                    const { fromTimeAvailability } = this.parent;
                    if (!fromTimeAvailability || !value) return true;

                    const [fromHours, fromMinutes] = fromTimeAvailability.split(':');
                    const [toHours, toMinutes] = value.split(':');

                    const fromTotalMinutes = parseInt(fromHours, 10) * 60 + parseInt(fromMinutes, 10);
                    const toTotalMinutes = parseInt(toHours, 10) * 60 + parseInt(toMinutes, 10);

                    return toTotalMinutes > fromTotalMinutes;
                }
            ),
        });
    };

    const dispatch = useDispatch<AppDispatch>();
    const { loading, error } = useSelector((state: RootState) => state.teleradiologistResponse);
    const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);
    const fileInputRef = useRef(null);
    const { uiNotificationService } = servicesManager.services

    const [countryCode, setCountryCode] = useState(['+91']); // Default country code
    const [department, setDepartment] = useState([])
    const [modalities, setModalities] = useState([]);
    const [selectedCountryCode, setSelectedCountryCode] = useState<string>('+91');
    const [regions, setRegions] = useState([]);
    const [days, setDays] = useState([]);
    const [gender, setGender] = useState([]);

    const [edit, setIsEdit] = useState(false);
    const [modal, setModal] = useState(false);
    const [title, setTitle] = useState('');
    const [image, setImage] = useState('');
    const [load, setLoad] = useState(false);
    const isDarkMode = useTheme();

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImage(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };
    const navigate = useNavigate();
    const location = useLocation();
    const { userData } = location?.state || {};

    useEffect(() => {
        fetchCommonData();
        dispatch(getBillingGeneralSettings());
        if (userData && Object.keys(userData).length > 0) {
            setImage(userData?.profilePic)
            setIsEdit(true);
        }
    }, [userData]);

    useEffect(() => {
        if (billingGeneralSettingsData?.status === 'success' && billingGeneralSettingsData?.result?.currency) {
            const newCountryCode = getCurrencyContact(billingGeneralSettingsData.result.currency);
            setSelectedCountryCode(newCountryCode);
        }
    }, [billingGeneralSettingsData]);

    useEffect(() => {
        setLoad(true);
        const timer = setTimeout(() => setLoad(false), 900);
        return () => clearTimeout(timer);
    }, []);

    const fetchCommonData = async () => {
        try {
            const commonData: any = await apiRequest({ method: 'POST', endUrl: _commonUrls.list, body: {}, headerType: 'json' })
            if (commonData?.status) {
                setCountryCode(commonData?.result?.countryCode);
                setDepartment(commonData?.result?.department);
                let allLabels = commonData?.result?.modality
                    ?.map(modality => modality.label)
                    .join(", ");
                let modalityData = [{ value: "Multi Speciality", label: "Multi Speciality", meta: allLabels }, ...commonData?.result?.modality];
                setModalities(modalityData);
                let allRegions = commonData?.result?.studyRegions
                    ?.map(modality => modality.label)
                    .join(", ");
                let studyRegionData = [{ value: "All", label: "All", meta: allRegions }, ...commonData?.result?.studyRegions];
                setRegions(studyRegionData);
                const formattedDays = commonData?.result?.day.map((day: string) => ({
                    value: day,
                    label: day,
                }));
                setDays(formattedDays);
                setGender(commonData?.result?.gender);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handleSaveForm = async (values) => {
        let obj = {
            name: values?.name,
            radiologistType: values?.radiologistType,
            contactNumberCountryCode: values?.contactNumberCountryCode,
            whatsappCountryCode: values?.whatsappCountryCode,
            role: values?.userRole,
            hprId: values?.hprId,
            gender: values.gender,
            email: values?.email,
            modality: values?.modality,
            studyRegion: values?.region,
            licenseNumber: values?.licenseNumber,
            contactNumber: values?.contactNumber,
            dob: values?.dob?.format('YYYY-MM-DD'),
            department: values?.department,
            yearsOfExperience: values?.yearsOfExperience,
            whatsappNumber: values?.whatsappNumber,
            availabilityDay: values?.availabilityDay,
            fromTimeAvailability: values?.fromTimeAvailability,
            toTimeAvailability: values?.toTimeAvailability,
            hospitalId: sessionStorage.getItem("hospitalId") || localStorage.getItem('hospitalId'),

        };
        if (edit) {
            obj['_id'] = userData?._id;
        }
        setLoad(true);
        try {
            const response = await dispatch(teleradiologistApi(obj)).unwrap();
            setLoad(false);
            if (response?.status) {
                setModal(true);
                setTitle(response?.msg);
                setTimeout(() => {
                    setTitle("");
                    navigate("/teleradiologist");
                }, 500);
            } else {
                uiNotificationService.show({
                    title: "Error",
                    type: "error",
                    message: response?.msg || "Something went wrong",
                    position: "topRight"
                });
            }
        } catch (error) {
            setLoad(false);
            uiNotificationService.show({
                title: "Error",
                type: "error",
                message: "Failed to save form. Please try again.",
                position: "topRight"
            });
        }
    };

    const cancel = () => {
        navigate("/teleradiologist");
    }

    const defaultCountryCode = billingGeneralSettingsData?.status && billingGeneralSettingsData?.result?.currency ? getCurrencyContact(billingGeneralSettingsData.result.currency) : '+91';

    let initialValues = {
        name: userData?.name || '',
        userRole: userData?.role || "",
        contactNumberCountryCode: defaultCountryCode,
        whatsappCountryCode: defaultCountryCode,
        radiologistType: userData?.radiologistType || '',
        hprId: userData?.hprId || '',
        gender: userData?.gender || '',
        email: userData?.email || '',
        modality: userData?.modality || [],
        region: userData?.studyRegion || [],
        contactNumber: getLocalPhoneNumber(userData?.contactNumber),
        dob: userData?.dob ? dayjs(userData?.dob) : null,
        department: userData?.department || '',
        yearsOfExperience: userData?.yearsOfExperience || '',
        whatsappNumber: getLocalPhoneNumber(userData?.whatsappNumber),
        licenseNumber: userData?.licenseNumber || '',
        sameAsContact: (userData?.contactNumber === userData?.whatsappNumber && userData?.whatsappNumber) ? true : false,
        availabilityDay: userData?.availabilityDay || [],
        fromTimeAvailability: userData?.fromTimeAvailability || '',
        toTimeAvailability: userData?.toTimeAvailability || '',
    };

    return (
        <div className='page page__full' style={
            isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
        }>
            <Header isBack={true} title={edit ? "Edit Teleradiologist" : "Add Teleradiologist"}></Header>
            <div className='page__body'>
                <div className='details'>
                    <div className='form form-innersec'>
                        <section className='form__section'>
                            <h1 className='form__section__title'>{edit ? "Edit" : "Add"} Teleradiologist Details</h1>
                            <div className="formRow">
                                <div className="formCol">
                                    <div className="form__Field">
                                        <label className="form__uploadProfile" htmlFor="uploadProfile">
                                            {image && <img className="form__uploadProfile__picture" src={image} alt="" />}
                                            <img className="form__uploadProfile__picture" src={ICONS.defaultUserIcon} alt="" />
                                            <span className="form__uploadProfile__overlay">
                                                <svg width="17" height="15" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M15.4782 12.1589C15.4782 12.5125 15.3377 12.8516 15.0877 13.1017C14.8376 13.3517 14.4985 13.4922 14.1449 13.4922H2.14486C1.79124 13.4922 1.4521 13.3517 1.20205 13.1017C0.951999 12.8516 0.811523 12.5125 0.811523 12.1589V4.82552C0.811523 4.4719 0.951999 4.13276 1.20205 3.88271C1.4521 3.63266 1.79124 3.49219 2.14486 3.49219H4.81152L6.14486 1.49219H10.1449L11.4782 3.49219H14.1449C14.4985 3.49219 14.8376 3.63266 15.0877 3.88271C15.3377 4.13276 15.4782 4.4719 15.4782 4.82552V12.1589Z" stroke="white" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path>
                                                    <path d="M8.14486 10.8255C9.61762 10.8255 10.8115 9.63161 10.8115 8.15885C10.8115 6.68609 9.61762 5.49219 8.14486 5.49219C6.6721 5.49219 5.47819 6.68609 5.47819 8.15885C5.47819 9.63161 6.6721 10.8255 8.14486 10.8255Z" stroke="white" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path>
                                                </svg>
                                            </span>
                                        </label>
                                        <input type="file" id="uploadProfile" className="form__uploadfiled" ref={fileInputRef}
                                            onChange={handleImageChange} />
                                    </div>
                                </div>
                            </div>
                            <Formik
                                initialValues={initialValues}
                                validationSchema={Yup.lazy((values) => values.userRole !== 'Resident' ? getValidationSchema(values.role) : getResidentValidationSchema(values.role))}
                                onSubmit={handleSaveForm}
                            >
                                {({
                                    errors,
                                    values,
                                    handleChange,
                                    handleBlur,
                                    touched,
                                    setFieldValue,
                                    setFieldError,
                                    setFieldTouched,
                                }) => {
                                    { console.log('errors', errors) }
                                    useEffect(() => {
                                        setFieldValue('contactNumberCountryCode', selectedCountryCode);
                                        setFieldValue('whatsappCountryCode', selectedCountryCode);
                                    }, [selectedCountryCode]);
                                    return (
                                        <Form >
                                            <div className='formRow formRow__2'>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label" htmlFor='radiologistType' >
                                                            Radiologist Type
                                                            <span className="required-asterisk">*</span>
                                                        </label>
                                                        <select
                                                            className="form__select form__select--sm"
                                                            style={customStyle.select}
                                                            id="radiologistType"
                                                            name="radiologistType"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.radiologistType}
                                                            autoComplete="off"
                                                            disabled={edit ? true : false}
                                                        >
                                                            <option value="" disabled>Select</option>
                                                            <option value="Internal">Internal</option>
                                                            <option value="External">External</option>
                                                        </select>
                                                    </div>
                                                    <ErrorMessage name="radiologistType" component="div" className="error text-error" />
                                                </div>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label" htmlFor='userRole' >
                                                            User Role
                                                            <span className="required-asterisk">*</span>
                                                        </label>
                                                        <select
                                                            className="form__select form__select--sm"
                                                            style={customStyle.select}
                                                            id="userRole"
                                                            name="userRole"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.userRole}
                                                            autoComplete="off"
                                                            disabled={edit ? true : false}
                                                        >
                                                            <option value="" disabled>Select</option>
                                                            <option value="Resident">Resident</option>
                                                            <option value="Radiologist">Radiologist</option>
                                                        </select>
                                                    </div>
                                                    <ErrorMessage name="userRole" component="div" className="error text-error" />
                                                </div>

                                            </div>
                                            <div className="formRow formRow__2">
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label" htmlFor='name'>
                                                            Name
                                                            <span className="required-asterisk">*</span>
                                                        </label>
                                                        <input className="form__input form__input--sm"
                                                            type="text"
                                                            id="name"
                                                            name="name"
                                                            placeholder="Name"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.name}
                                                            autoComplete='off'
                                                        />
                                                    </div>
                                                    <ErrorMessage name="name" component="div" className="error text-error" />
                                                </div>

                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label" htmlFor='gender'>Gender</label>
                                                        <div className="form__radio mt-5">
                                                            {gender.map((option) => (
                                                                <div className="form__radio__option mb_0">
                                                                    <input type="radio" name="gender"
                                                                        value={option.value}
                                                                        checked={option.value === values?.gender}
                                                                        onChange={handleChange}
                                                                        className="form__radio__input" />
                                                                    <label className="form__radio__label" htmlFor="addAgentWeightZone">{option.label}</label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='formRow formRow__2'>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label" htmlFor='hprId'>
                                                            HPR ID
                                                        </label>
                                                        <input
                                                            className="form__input form__input--sm"
                                                            type="text"
                                                            id="hprId"
                                                            name="hprId"
                                                            placeholder="HPR ID"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.hprId}
                                                            autoComplete='off'
                                                        />
                                                    </div>
                                                </div>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label">
                                                            Email ID<span className="required-asterisk">*</span>
                                                        </label>
                                                        <input
                                                            className="form__input form__input--sm"
                                                            type="text"
                                                            id="email"
                                                            name="email"
                                                            placeholder="Email ID"
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.email}
                                                            disabled={edit ? true : false}
                                                            autoComplete='off'
                                                        />
                                                        <ErrorMessage name="email" component="div" className="error text-error" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className='formRow formRow__2'>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label" htmlFor='contactNumber'>
                                                            Contact Number<span className="required-asterisk">*</span>
                                                        </label>
                                                        <div className='form__groupField form__groupField--phonenumber'>
                                                            <select className="form__select form__select--sm" id="contactNumberCountryCode"
                                                                name="contactNumberCountryCode"
                                                                value={values.contactNumberCountryCode}
                                                                style={customStyle.select}
                                                                onChange={(e) => {
                                                                    handleChange(e);
                                                                    if (values.sameAsContact || (values.contactNumber === values.whatsappNumber && values.whatsappNumber)) {
                                                                        setFieldValue('whatsappCountryCode', e.target.value);
                                                                        setFieldValue('whatsappNumber', values.contactNumber);
                                                                        setFieldValue('sameAsContact', true);
                                                                    }
                                                                }}>
                                                                {countryCode.map((option) => (
                                                                    <option 
                                                                    key={option} 
                                                                    value={option} 
                                                                    disabled={billingGeneralSettingsData?.status === 'success' && option !== selectedCountryCode}
                                                                    >
                                                                    {option}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <input type="text"
                                                                className="form__input form__input--sm"
                                                                id="contactNumber"
                                                                name="contactNumber"
                                                                placeholder="Contact Number"
                                                                maxLength={10}
                                                                onChange={(e) => {
                                                                    handleChange(e);
                                                                    if (values.sameAsContact || (e.target.value === values.whatsappNumber && values.whatsappNumber)) {
                                                                        setFieldValue('whatsappNumber', e.target.value);
                                                                        setFieldValue('sameAsContact', true);
                                                                    } else {
                                                                        setFieldValue('sameAsContact', false);
                                                                    }
                                                                }}
                                                                onBlur={handleBlur}
                                                                value={values.contactNumber}
                                                                autoComplete='off'>
                                                            </input>
                                                        </div>
                                                        <ErrorMessage name="contactNumber" component="div" className="error text-error" />
                                                    </div>
                                                </div>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label">
                                                            Date of Birth<span className="required-asterisk">*</span>
                                                        </label>
                                                        <div className="form__date-container">
                                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                <Field name="dob" style={{ width: '100%' }}>
                                                                    {({ field }) => (
                                                                        <DatePicker
                                                                            sx={{
                                                                                width: '100%',
                                                                                '& .MuiInputBase-root': {
                                                                                    backgroundColor: '#F6FFFF',
                                                                                    borderRadius: '2rem'
                                                                                },
                                                                                '& .MuiInputBase-input': {
                                                                                    padding: '10px',
                                                                                },
                                                                            }}
                                                                            {...field}
                                                                            value={field.value || null}
                                                                            format="DD/MM/YYYY"
                                                                            maxDate={dayjs()}
                                                                            onChange={(newValue) => {
                                                                                setFieldValue(field.name, newValue);
                                                                            }}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    error={touched.date && Boolean(errors.date)}
                                                                                    helperText={touched.date && errors.date}
                                                                                />
                                                                            )}
                                                                        />
                                                                    )}
                                                                </Field>
                                                            </LocalizationProvider>
                                                        </div>
                                                        <ErrorMessage name="dob" component="div" className="error text-error" />

                                                    </div>
                                                </div>
                                            </div>
                                            <div className='formRow formRow__2'>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label">
                                                            Department
                                                        </label>
                                                        <select
                                                            id="department"
                                                            name="department"
                                                            className="form__select form__select--sm"
                                                            style={customStyle.select}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.department}
                                                            autoComplete='off'
                                                        >
                                                            <option value="" disabled>Department</option>
                                                            {department.map((option) => (
                                                                <option key={option} value={option}>
                                                                    {option}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label" htmlFor='numberOfHealthcareProfessionals'>Years of Experience</label>
                                                        <input
                                                            className="form__input form__input--sm"
                                                            type="number"
                                                            id="yearsOfExperience"
                                                            name="yearsOfExperience"
                                                            placeholder="--"
                                                            min='0'
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.yearsOfExperience}
                                                            autoComplete='off'
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {values?.userRole !== 'Resident' ? (
                                                <>
                                                    <div className="formRow formRow__2">
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='modality'>Expertise<span className="required-asterisk">*</span></label>
                                                                <Select
                                                                    id='modality'
                                                                    placeholder='Select'
                                                                    className='custom-bg-white'
                                                                    isMulti
                                                                    isClearable={false}
                                                                    isSearchable
                                                                    closeMenuOnSelect={false}
                                                                    hideSelectedOptions={false}
                                                                    options={modalities}
                                                                    value={values.modality}
                                                                    onChange={(selectedOptions) => {
                                                                        const newModalities = selectedOptions ? selectedOptions.map(option => option) : [];
                                                                        setFieldValue('modality', newModalities);
                                                                    }}
                                                                />
                                                            </div>
                                                            <ErrorMessage name="modality" component="div" className="error text-error" />
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='region'>Study Region</label>
                                                                <Select
                                                                    id='region'
                                                                    placeholder='Select'
                                                                    className='custom-bg-white'
                                                                    isMulti
                                                                    isClearable={false}
                                                                    isSearchable
                                                                    closeMenuOnSelect={false}
                                                                    hideSelectedOptions={false}
                                                                    options={regions}
                                                                    value={values.region}
                                                                    onChange={(selectedOptions) => {
                                                                        const newRegions = selectedOptions ? selectedOptions.map(option => option) : [];
                                                                        setFieldValue('region', newRegions);
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='formRow formRow__2'>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='whatsappNumber'>Whatsapp Number</label>
                                                                <div className="form__groupField form__groupField--phonenumber">
                                                                    <select
                                                                        id="whatsappCountryCode"
                                                                        style={customStyle.select}
                                                                        className="form__select form__select--sm"
                                                                        name="whatsappCountryCode"
                                                                        value={values.whatsappCountryCode}
                                                                        onChange={handleChange}
                                                                    >
                                                                        {countryCode.map((option) => (
                                                                            <option 
                                                                            key={option} 
                                                                            value={option} 
                                                                            disabled={billingGeneralSettingsData?.status === 'success' && option !== selectedCountryCode}
                                                                            >
                                                                            {option}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                    <input
                                                                        type="text"
                                                                        className="form__input form__input--sm"
                                                                        id="whatsappNumber"
                                                                        name="whatsappNumber"
                                                                        placeholder="Whatsapp Number"
                                                                        maxLength={10}
                                                                        onChange={(e) => {
                                                                            handleChange(e);
                                                                            if (e.target.value === values.contactNumber && values.contactNumber) {
                                                                                setFieldValue('sameAsContact', true);
                                                                            } else {
                                                                                setFieldValue('sameAsContact', false);
                                                                            }
                                                                        }}
                                                                        onBlur={handleBlur}
                                                                        value={values.whatsappNumber}
                                                                        autoComplete='off'>
                                                                    </input>
                                                                </div>
                                                                <div className="form__checkboxSubField">
                                                                    <div className="form__checkbox">
                                                                        <div className="form__checkbox__option m_0">
                                                                            <input className="form__checkbox__input" id='sameAsContact' checked={values.sameAsContact} type="checkbox" name="sameAsContact" value="sameAsContact" onChange={(e) => {
                                                                                setFieldValue('sameAsContact', e.target.checked);
                                                                                if (e.target.checked) {
                                                                                    setFieldValue('whatsappNumber', values.contactNumber);
                                                                                    setFieldValue('whatsappCountryCode', values?.contactNumberCountryCode);
                                                                                } else {
                                                                                    setFieldValue('whatsappNumber', '');
                                                                                    setFieldValue('whatsappCountryCode', '');
                                                                                }
                                                                            }} />
                                                                            <label htmlFor="sameAsContact" className="form__checkbox__label"> Same as Contact Number</label>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <ErrorMessage name="whatsappNumber" component="div" className="error text-error" />
                                                            </div>
                                                        </div>
                                                        <div className='formCol'>
                                                            <div className="form__Field">
                                                                <label className="form__label" htmlFor='numberOfHealthcareProfessionals'>
                                                                    State Medical Council Registration License Number
                                                                    {values?.role !== 'Admin' && values?.role !== 'Front Desk' && <span className="required-asterisk">*</span>}
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    className="form__input form__input--sm"
                                                                    id="licenseNumber"
                                                                    name="licenseNumber"
                                                                    placeholder="License Number"
                                                                    onChange={handleChange}
                                                                    onBlur={handleBlur}
                                                                    value={values.licenseNumber}
                                                                    autoComplete='off'
                                                                />
                                                                <ErrorMessage name="licenseNumber" component="div" className="error text-error" />
                                                            </div>
                                                            <div className='form__Field'></div>
                                                        </div>
                                                    </div>
                                                </>
                                            ) : <></>}
                                            <div className='formRow formRow__2'>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label" htmlFor='availabilityDay'>
                                                            Availability (Days)
                                                            <span className="required-asterisk">*</span>
                                                        </label>
                                                        <Select
                                                            id='availabilityDay'
                                                            placeholder='Select'
                                                            className='custom-bg-white'
                                                            isMulti
                                                            isClearable={false}
                                                            isSearchable
                                                            closeMenuOnSelect={false}
                                                            hideSelectedOptions={false}
                                                            options={days}
                                                            value={values.availabilityDay}
                                                            onChange={(selectedOptions) => {
                                                                const newAvailabilityDay = selectedOptions ? selectedOptions.map(option => option) : [];
                                                                setFieldValue('availabilityDay', newAvailabilityDay);
                                                            }}
                                                        />
                                                    </div>
                                                    <ErrorMessage name="availabilityDay" component="div" className="error text-error" />
                                                </div>
                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label" htmlFor='fromTimeAvailability'>
                                                            From Timing
                                                            <span className="required-asterisk">*</span>
                                                        </label>
                                                        <input
                                                            className="form__input form__input--sm"
                                                            type="time"
                                                            id="fromTimeAvailability"
                                                            name="fromTimeAvailability"
                                                            min='00:00'
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.fromTimeAvailability}
                                                            defaultValue="00:00"
                                                            autoComplete='off'
                                                        />
                                                    </div>
                                                    <ErrorMessage name="fromTimeAvailability" component="div" className="error text-error" />
                                                </div>
                                            </div>
                                            <div className="formRow formRow__2">

                                                <div className='formCol'>
                                                    <div className="form__Field">
                                                        <label className="form__label" htmlFor='toTimeAvailability'>
                                                            To Timing
                                                            <span className="required-asterisk">*</span>
                                                        </label>
                                                        <input
                                                            className="form__input form__input--sm"
                                                            type="time"
                                                            id="toTimeAvailability"
                                                            name="toTimeAvailability"
                                                            min='00:00'
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            value={values.toTimeAvailability}
                                                            defaultValue="00:00"
                                                            autoComplete='off'
                                                        />
                                                    </div>
                                                    <ErrorMessage name="toTimeAvailability" component="div" className="error text-error" />
                                                </div>
                                            </div>

                                            <div className='btn__group'>
                                                <button
                                                    className='btn btn__primary'
                                                    type="submit"
                                                >
                                                    {edit ? "Update" : "Save"}
                                                </button>
                                                <button
                                                    className='btn btn__white'
                                                    onClick={cancel}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </Form>
                                    )
                                }}
                            </Formik>
                        </section>

                    </div>
                </div>
            </div>

            <Loader isLoaderOpen={load} />
            <SuccessModal
                isSuccessModalOpen={modal}
                title={title}
                handleCloseModal={() => setModal(false)}
            />
        </div>
    )
}

export default AddEditRadiologist
